import { format, isValid } from 'date-fns';
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Copy } from 'lucide-react';

import type { AuditLog } from '~features/audit-logs/api/get-audit-logs';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Badge } from '~shared/shadcn/ui/badge';
import { Separator } from '~shared/shadcn/ui/separator';
import { copyToClipboard } from '~shared/utils/clipboard';
import { isValidIsoDate } from '~shared/utils/date-time';
import { useStudioDateFormatter } from '~shared/hooks/useStudioDateFormatter';

interface AuditLogDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditLog: AuditLog | null;
}

export function AuditLogDetailsModal({
  open,
  onOpenChange,
  auditLog,
}: AuditLogDetailsModalProps) {
  const { formatInStudioTz } = useStudioDateFormatter();

  const changes = auditLog?.changes;
  const eventTypeLower = auditLog?.eventType?.toLowerCase() ?? '';

  const formattedDate = React.useMemo(() => {
    if (!auditLog?.createdAt) return '—';
    try {
      return formatInStudioTz(auditLog.createdAt, "MMM dd, yyyy 'at' h:mm aa");
    } catch {
      return auditLog.createdAt;
    }
  }, [auditLog?.createdAt, formatInStudioTz]);

  const hasChanges = React.useMemo(() => {
    if (eventTypeLower === 'created') {
      return !!changes?.after && Object.keys(changes.after).length > 0;
    }
    if (eventTypeLower === 'updated') {
      return (
        !!(changes?.before && Object.keys(changes.before).length > 0) ||
        !!(changes?.after && Object.keys(changes.after).length > 0) ||
        !!(changes?.fields && changes.fields.length > 0)
      );
    }
    if (eventTypeLower === 'deleted') {
      return !!changes?.before && Object.keys(changes.before).length > 0;
    }
    return false;
  }, [changes, eventTypeLower]);

  if (!auditLog) return null;

  const {
    eventType,
    resourceName,
    paymentDisplayName,
    resourceId,
    actorName,
    actorEmail,
    parentActorDisplayName,
    parentActorEmail,
    metadata,
    resourceType,
    enrollmentDisplayName,
    studentDisplayName,
  } = auditLog;
  const isEnrollmentResource = resourceType?.toLowerCase() === 'enrollment';
  const enrollmentClassTitle = isEnrollmentResource
    ? enrollmentDisplayName || resourceName
    : resourceName;
  const formatAuditValue = (value: string) =>
    value
      .split(/[-_]/g)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

  // Helper function to get nested value using dot notation (e.g., "address.city")
  const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
    const keys = path.split('.');
    let current: unknown = obj;
    for (const key of keys) {
      if (current && typeof current === 'object' && !Array.isArray(current) && key in current) {
        current = (current as Record<string, unknown>)[key];
      } else {
        return undefined;
      }
    }
    return current;
  };

  // Helper function to format values for display
  const formatValue = (value: unknown): string => {
    if (value === undefined || value === null) {
      return 'null';
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return '[object Object]';
      }
    }

    if (typeof value === 'string' && isValidIsoDate(value)) {
      const date = new Date(value);
      if (isValid(date)) {
        return format(date, 'MMM dd, yyyy');
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    return String(value);
  };

  // Helper component to render nested objects in a more readable way
  const renderNestedValue = (value: unknown, depth = 0): React.ReactNode => {
    if (value === undefined || value === null) {
      return <span className='text-muted-foreground'>null</span>;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className='text-muted-foreground'>[]</span>;
      }
      return (
        <div className='ml-2 space-y-1'>
          {value.map((item, index) => (
            <div key={index} className='border-l-2 border-muted pl-2'>
              {renderNestedValue(item, depth + 1)}
            </div>
          ))}
        </div>
      );
    }
    if (typeof value === 'object') {
      const entries = Object.entries(value);
      if (entries.length === 0) {
        return <span className='text-muted-foreground'>{'{}'}</span>;
      }
      return (
        <div className={`ml-2 space-y-1 ${depth > 0 ? 'border-l-2 border-muted pl-2' : ''}`}>
          {entries.map(([key, val]) => (
            <div key={key} className='space-y-1'>
              <span className='text-xs font-medium'>{key}:</span>
              <div className='ml-2'>{renderNestedValue(val, depth + 1)}</div>
            </div>
          ))}
        </div>
      );
    }
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    return <span>{String(value)}</span>;
  };

  // Determine event type badge variant
  const getEventBadgeVariant = () => {
    if (eventTypeLower === 'created') return 'default';
    if (eventTypeLower === 'deleted') return 'destructive';
    if (eventTypeLower === 'updated') return 'secondary';
    return 'outline';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] max-w-3xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Audit Log Details</DialogTitle>
          <DialogDescription>
            Detailed information about this audit log entry
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-6'>
          {/* Basic Information */}
          <div className='space-y-4'>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <div className='mb-1 text-sm font-medium text-muted-foreground'>
                  Resource Name
                </div>
                <div className='flex flex-col gap-1 text-sm font-semibold'>
                  {resourceType?.toLowerCase() === 'student' &&
                  eventType !== 'deleted' ? (
                    <Link
                      params={{ studentId: resourceId }}
                      to={`/students/$studentId/details`}
                      className='text-sm text-secondary underline'
                    >
                      {resourceName || '—'}
                    </Link>
                  ) : isEnrollmentResource ? (
                    <>
                      <span>{enrollmentClassTitle || '—'}</span>
                      {studentDisplayName ? (
                        <span className='text-xs font-normal text-muted-foreground'>
                          Student: {studentDisplayName}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    paymentDisplayName || resourceName || '—'
                  )}
                </div>
              </div>
            </div>

            <div className='grid grid-cols-2 gap-4'>
            <div>
                <div className='mb-1 text-sm font-medium text-muted-foreground'>
                  Resource ID
                </div>
                {resourceId ? (
                  <div className='flex items-center gap-2'>
                    <span className='font-mono text-xs'>{resourceId}</span>
                    <button
                      type='button'
                      onClick={async () => {
                        const success = await copyToClipboard(resourceId);
                        if (!success) {
                          console.error('Failed to copy resource ID');
                        }
                      }}
                      className='inline-flex items-center justify-center rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                      aria-label='Copy resource ID'
                    >
                      <Copy className='size-3.5' />
                    </button>
                  </div>
                ) : (
                  <div className='font-mono text-xs'>—</div>
                )}
              </div>
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div>
                <div className='mb-1 text-sm font-medium text-muted-foreground'>
                  Resource Type
                </div>
                <div className='text-sm font-semibold'>
                  {resourceType ? formatAuditValue(resourceType) : '—'}
                </div>
              </div>
              <div>
                <div className='mb-1 text-sm font-medium text-muted-foreground'>
                  Actor
                </div>
                <div className='flex flex-col gap-0.5 text-sm font-semibold'>
                  <span>
                    {actorName ||
                      parentActorDisplayName ||
                      actorEmail ||
                      parentActorEmail ||
                      '—'}
                  </span>
                  {(actorName?.trim() || parentActorDisplayName?.trim()) &&
                    (actorEmail?.trim() || parentActorEmail?.trim()) && (
                      <span className='text-xs font-normal text-muted-foreground'>
                        {actorEmail?.trim() || parentActorEmail}
                      </span>
                    )}
                </div>
              </div>
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div>
                <div className='mb-1 text-sm font-medium text-muted-foreground'>
                  Event Type
                </div>
                <Badge variant={getEventBadgeVariant()}>
                  {formatAuditValue(eventType)}
                </Badge>
              </div>
              <div>
                <div className='mb-1 text-sm font-medium text-muted-foreground'>
                  Date & Time
                </div>
                <div className='text-sm'>{formattedDate}</div>
              </div>
            </div>
          </div>

          <Separator />

          {metadata && Object.keys(metadata).length > 0 && (
            <>
              <div className='space-y-4'>
                <div className='text-lg font-semibold'>Metadata</div>
                <div className='rounded-md border bg-muted/20 p-4 text-sm'>
                  {renderNestedValue(metadata)}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Changes Section */}
          {hasChanges && (
            <div className='space-y-4'>
              <div className='text-lg font-semibold'>Changes</div>

              {/* For Update Events */}
              {eventTypeLower === 'updated' && (
                <div className='space-y-4'>
                  {/* Fields that were updated */}
                  {changes?.fields && changes.fields.length > 0 && (
                    <div>
                      <div className='mb-2 text-sm font-medium text-muted-foreground'>
                        Updated Fields
                      </div>
                      <div className='flex flex-wrap gap-2'>
                        {changes.fields.map((field, index) => (
                          <Badge key={index} variant='outline'>
                            {field}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Before and After comparison */}
                  {changes?.before && changes.after && (
                    <div className='space-y-3'>
                      <div className='text-sm font-medium text-muted-foreground'>
                        Field Changes
                      </div>
                      <div className='space-y-3 rounded-lg border bg-muted/30 p-4'>
                        {/* If fields array exists, only show those fields */}
                        {changes.fields && changes.fields.length > 0 ? (
                          changes.fields.map((fieldKey) => {
                            // Handle dot notation paths (e.g., "address.city")
                            const beforeValue = fieldKey.includes('.')
                              ? getNestedValue(changes.before ?? {}, fieldKey)
                              : changes.before?.[fieldKey];
                            const afterValue = fieldKey.includes('.')
                              ? getNestedValue(changes.after ?? {}, fieldKey)
                              : changes.after?.[fieldKey];

                            const isNestedObject = 
                              (typeof beforeValue === 'object' && beforeValue !== null && !Array.isArray(beforeValue)) ||
                              (typeof afterValue === 'object' && afterValue !== null && !Array.isArray(afterValue));

                            return (
                              <div key={fieldKey} className='space-y-1'>
                                <div className='text-sm font-medium'>{fieldKey}</div>
                                {isNestedObject ? (
                                  // Render nested objects in a more structured way
                                  <div className='grid grid-cols-2 gap-4 text-xs'>
                                    <div>
                                      <div className='mb-1 text-muted-foreground'>
                                        Before
                                      </div>
                                      <div className='max-h-48 overflow-y-auto break-words rounded border border-destructive/20 bg-destructive/10 p-2'>
                                        {renderNestedValue(beforeValue)}
                                      </div>
                                    </div>
                                    <div>
                                      <div className='mb-1 text-muted-foreground'>
                                        After
                                      </div>
                                      <div className='max-h-48 overflow-y-auto break-words rounded border border-green-500/20 bg-green-500/10 p-2'>
                                        {renderNestedValue(afterValue)}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  // Render simple values with JSON formatting
                                  <div className='grid grid-cols-2 gap-4 text-xs'>
                                    <div>
                                      <div className='mb-1 text-muted-foreground'>
                                        Before
                                      </div>
                                      <div className='break-words rounded border border-destructive/20 bg-destructive/10 p-2 font-mono'>
                                        {formatValue(beforeValue)}
                                      </div>
                                    </div>
                                    <div>
                                      <div className='mb-1 text-muted-foreground'>
                                        After
                                      </div>
                                      <div className='break-words rounded border border-green-500/20 bg-green-500/10 p-2 font-mono'>
                                        {formatValue(afterValue)}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          // If no fields array, show all changed fields
                          Object.keys(changes.after).map((key) => {
                            const beforeValue = changes.before?.[key];
                            const afterValue = changes.after?.[key];
                            const hasChanged =
                              JSON.stringify(beforeValue) !==
                              JSON.stringify(afterValue);

                            if (!hasChanged && beforeValue !== undefined) {
                              return null;
                            }

                            return (
                              <div key={key} className='space-y-1'>
                                <div className='text-sm font-medium'>{key}</div>
                                <div className='grid grid-cols-2 gap-4 text-xs'>
                                  <div>
                                    <div className='mb-1 text-muted-foreground'>
                                      Before
                                    </div>
                                    <div className='break-words rounded border border-destructive/20 bg-destructive/10 p-2 font-mono'>
                                      {formatValue(beforeValue)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className='mb-1 text-muted-foreground'>
                                      After
                                    </div>
                                    <div className='break-words rounded border border-green-500/20 bg-green-500/10 p-2 font-mono'>
                                      {formatValue(afterValue)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* For Create Events */}
              {eventTypeLower === 'created' && changes?.after && (
                <div className='space-y-3'>
                  <div className='text-sm font-medium text-muted-foreground'>
                    Created Resource Data
                  </div>
                  <div className='space-y-3 rounded-lg border bg-muted/30 p-4'>
                    {Object.entries(changes.after).map(([key, value]) => {
                      const isNestedObject = typeof value === 'object' && value !== null && !Array.isArray(value);
                      return (
                        <div key={key} className='space-y-1'>
                          <div className='text-sm font-medium'>{key}</div>
                          <div className='max-h-48 overflow-y-auto break-words rounded border border-green-500/20 bg-green-500/10 p-2 text-xs'>
                            {isNestedObject ? renderNestedValue(value) : <span className='font-mono'>{formatValue(value)}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* For Delete Events */}
              {eventTypeLower === 'deleted' && changes?.before && (
                <div className='space-y-3'>
                  <div className='text-sm font-medium text-muted-foreground'>
                    Deleted Resource Data
                  </div>
                  <div className='space-y-3 rounded-lg border bg-muted/30 p-4'>
                    {Object.entries(changes.before).map(([key, value]) => {
                      const isNestedObject = typeof value === 'object' && value !== null && !Array.isArray(value);
                      return (
                        <div key={key} className='space-y-1'>
                          <div className='text-sm font-medium'>{key}</div>
                          <div className='max-h-48 overflow-y-auto break-words rounded border border-destructive/20 bg-destructive/10 p-2 text-xs'>
                            {isNestedObject ? renderNestedValue(value) : <span className='font-mono'>{formatValue(value)}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

